import { useState, useCallback, useRef } from 'react'

export const useStableUIState = () => {
  // Modal states that should persist across data updates
  const [isAnnotationModalOpen, setAnnotationModalOpen] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [aiDialogOpen, setAIDialogOpen] = useState(false)
  const [selectedAI, setSelectedAI] = useState(null)
  const [vcfIdState, setVcfIdState] = useState(null)

  // Error modal states
  const [errorModalOpen, setErrorModalOpen] = useState(false)
  const [neededBalance, setNeededBalance] = useState(-1)

  // Keep references to prevent closure issues
  const selectedFileRef = useRef(null)
  const isAnnotationModalOpenRef = useRef(false)

  // Update refs when state changes
  const updateSelectedFile = useCallback((file) => {
    setSelectedFile(file)
    selectedFileRef.current = file
  }, [])

  const updateAnnotationModalOpen = useCallback((open) => {
    setAnnotationModalOpen(open)
    isAnnotationModalOpenRef.current = open
  }, [])

  // Safe getters that handle cases where data might have updated
  const getSafeSelectedFile = useCallback(() => {
    return selectedFileRef.current
  }, [])

  const isModalCurrentlyOpen = useCallback(() => {
    return isAnnotationModalOpenRef.current
  }, [])

  // Handler for opening annotation modal
  const handleAnnotationModelOpen = useCallback(
    (row) => {
      updateSelectedFile(row)
      updateAnnotationModalOpen(true)
      setVcfIdState(row.vcf_id)
    },
    [updateSelectedFile, updateAnnotationModalOpen],
  )

  // Handler for closing annotation modal
  const handleAnnotationModelClose = useCallback(() => {
    updateAnnotationModalOpen(false)
    // Don't clear selectedFile immediately to prevent issues during closing animation
    setTimeout(() => {
      if (!isAnnotationModalOpenRef.current) {
        updateSelectedFile(null)
        setVcfIdState(null)
      }
    }, 300)
  }, [updateAnnotationModalOpen, updateSelectedFile])

  // AI dialog handlers
  const handleOpenAIDialog = useCallback((ai) => {
    setSelectedAI(ai)
    setAIDialogOpen(true)
  }, [])

  const handleCloseAIDialog = useCallback(() => {
    setAIDialogOpen(false)
    setTimeout(() => {
      if (!aiDialogOpen) {
        setSelectedAI(null)
      }
    }, 300)
  }, [aiDialogOpen])

  return {
    // State
    isAnnotationModalOpen,
    selectedFile,
    aiDialogOpen,
    selectedAI,
    vcfIdState,
    errorModalOpen,
    neededBalance,

    // Setters
    setErrorModalOpen,
    setNeededBalance,

    // Handlers
    handleAnnotationModelOpen,
    handleAnnotationModelClose,
    handleOpenAIDialog,
    handleCloseAIDialog,

    // Safe getters
    getSafeSelectedFile,
    isModalCurrentlyOpen,
  }
}
